<template>
  <div class="notifications">
    <button
        v-if="stickyToasts.length > 0"
        ref="closeAllBtn"
        class="close-all-btn"
        @click="closeAll"
    >
      ✕ Close all
    </button>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent'

// Only beep for client assignations (manual reassign + affiliate-inserted leads)
// and calendar events. Login, validation, ticket, monetary, system toasts stay silent.
const shouldPlayNotificationSound = (n) =>
    n.source === 'CALENDAR' ||
    (n.source === 'CLIENT' && n.content === 'ASSIGNED');

export default {
  name: 'NotificationHandler',
  components: { ToastificationContent },

  created() {
    this.$root.$on('system::notification', this.showNotification);
    this.$root.$on('system::notification::notify', this.onNewNotification);
  },

  beforeDestroy() {
    this.$root.$off('system::notification', this.showNotification);
    this.$root.$off('system::notification::notify', this.onNewNotification);
  },

  data() {
    return {
      stickyToasts: [], // ONLY sticky notifications
    }
  },

  watch: {
    stickyToasts() {
      this.$nextTick(() => {
        // First adjustment (after animation)
        setTimeout(() => this.positionCloseButton(), 500);
      });
    }
  }
  ,

  methods: {

    playNotificationSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();

        oscillator.connect(gain);
        gain.connect(ctx.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.1);

        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      } catch (e) {
        // AudioContext not available
      }
    },

    closeAll() {
      this.$toast.clear();
      this.stickyToasts = [];
    },

    positionCloseButton() {
      if (this.stickyToasts.length === 0) return;

      // Find first visible toast
      const toast = document.querySelector('.Vue-Toastification__toast');
      if (!toast) return;

      const rect = toast.getBoundingClientRect();
      const btn = this.$refs.closeAllBtn;

      if (!btn) return;

      const btnHeight = btn.offsetHeight || 30;

      btn.style.position = 'fixed';
      btn.style.left = `${rect.left}px`;
      btn.style.top = `${rect.top - btnHeight - 10}px`;
      btn.style.width = `${rect.width}px`;
      btn.style.zIndex = 999999;
    },

    onNewNotification(notification) {
      let base = `notifications.${notification.source}`;

      // FINANCE_ALERT is a single backend content value carrying the actual
      // action in params.financeAction ("cancel" / "fake") — prefer the
      // action-specific key set (FINANCE_ALERT_CANCEL / FINANCE_ALERT_FAKE)
      // when it exists so each alert renders its own message.
      let content = notification.content;
      const financeAction = notification.params?.financeAction;
      if (content === 'FINANCE_ALERT' && financeAction) {
        const derived = `FINANCE_ALERT_${String(financeAction).toUpperCase()}`;
        if (this.$te(`${base}.${derived}.title`)) {
          content = derived;
        }
      }
      let contentBase = `${base}.${content}`;

      // When the current user is a CC'd manager (not the actual assignee),
      // prefer "...ForManager" variants so the text reads "assigned to {name}"
      // instead of the assignee's "assigned to you" text. Falls back to the
      // base keys if no manager variant is defined for that content.
      const myId = this.$store.getters['user/myId'];
      const assignedUserId = notification.params?.assignedUserId;
      const isManagerCc = assignedUserId && myId && assignedUserId !== myId;

      const titleKey = isManagerCc && this.$te(`${contentBase}.titleForManager`)
          ? `${contentBase}.titleForManager`
          : `${contentBase}.title`;
      const bodyKey = isManagerCc && this.$te(`${contentBase}.bodyForManager`)
          ? `${contentBase}.bodyForManager`
          : `${contentBase}.body`;

      let systemNotification = {
        title: this.$t(titleKey, notification.params),
        message: this.$t(bodyKey, notification.params),
      };

      if (notification.params?.id) {
        systemNotification.stickId = notification.params.id;
      }

      if (this.$te(`${base}.color`)) {
        systemNotification.variant = this.$t(`${base}.color`);
      }
      if (this.$te(`${base}.icon`)) {
        systemNotification.icon = this.$t(`${base}.icon`);
      }
      if (this.$te(`${base}.iconType`)) {
        systemNotification.iconType = this.$t(`${base}.iconType`);
      }
      // A notification type may override its source's colour/icon (e.g. a red
      // CLIENT.LOGIN_FAILED alert among the blue client notifications).
      if (this.$te(`${contentBase}.color`)) {
        systemNotification.variant = this.$t(`${contentBase}.color`);
      }
      if (this.$te(`${contentBase}.icon`)) {
        systemNotification.icon = this.$t(`${contentBase}.icon`);
      }

      if (this.$te(`${base}.timeout`)) {
        systemNotification.timeout = this.$t(`${base}.timeout`);
      } else if (this.$te(`${base}.${notification.content}.timeout`)) {
        systemNotification.timeout = this.$t(`${base}.${notification.content}.timeout`);
      }

      if (shouldPlayNotificationSound(notification)) {
        this.playNotificationSound();
      }

      this.showNotification(systemNotification);
    },

    showNotification(notification) {
      let componentProps = {
        title: notification.title,
        text: notification.message,
        icon: notification.icon || (notification.variant === 'danger' ? 'AlertTriangleIcon' : 'EditIcon'),
        iconType: notification.iconType || 'feather',
        variant: notification.variant || 'success',
      };

      let toastOptions = {};

      // Sticky logic
      const isSticky = notification.stickId != null;

      if (isSticky) {
        toastOptions.id = notification.stickId;
        toastOptions.timeout = false; // sticky always
      } else {
        toastOptions.timeout = notification.timeout ? notification.timeout * 1000 : 3000;
      }

      // Track closing
      toastOptions.onClose = () => {
        if (isSticky) {
          this.stickyToasts = this.stickyToasts.filter(x => x !== notification.stickId);
        }
      };

      // CREATE / UPDATE toast
      if (!isSticky) {
        this.$toast(
            {
              component: ToastificationContent,
              props: componentProps,
              listeners: { click: () => console.log('Clicked!') }
            },
            toastOptions
        );
      } else {
        this.$toast.update(
            notification.stickId,
            {
              content: {
                component: ToastificationContent,
                props: componentProps,
                listeners: { click: () => console.log('Clicked!') }
              },
              options: toastOptions
            },
            true
        );
      }

      // Track sticky toast IDs (no duplicates)
      if (isSticky && !this.stickyToasts.includes(notification.stickId)) {
        this.stickyToasts.push(notification.stickId);
      }
    }
  }
}
</script>
<style scoped>
.close-all-btn {
  background: rgba(220, 70, 70, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  color: #fff;
  padding: 10px 0;

  border: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: none;
  border-radius: 10px 10px 0 0;

  cursor: pointer;
  font-size: 14px;
  text-align: center;
  letter-spacing: 0.3px;

  transition: all 0.2s ease;
  box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.35),
      0 0 8px rgba(255, 80, 80, 0.4);
}

.close-all-btn:hover {
  background: rgba(235, 90, 90, 0.78);
  box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.45),
      0 0 10px rgba(255, 100, 100, 0.55);
  transform: translateY(-1px);
}

.close-all-btn:active {
  transform: translateY(0);
  background: rgba(210, 60, 60, 0.9);
}
</style>


<style>
/* Push toast container slightly down so Close All sits above them */
.Vue-Toastification__container.top-right,
.Vue-Toastification__container.top-center,
.Vue-Toastification__container.top-left {
  margin-top: 50px !important;
}
.dark-layout .Vue-Toastification__toast {
  background-color: rgba(30, 30, 30, 0.55) !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;

  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 12px !important;

  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.45) !important;

  color: #fff !important;
}

.Vue-Toastification__toast {
  background-color: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;

  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 12px !important;

  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18) !important;

  color: #222 !important;
}

</style>
